import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CancelDraftButton } from '../components/CancelDraftButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { errorMessage } from '../components/LoadError';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, AlertTriangle, Boxes, Package, Container, IndianRupee, Layers, TrendingUp, Truck, X, Lock } from 'lucide-react';
import { Term, PLANNER_TERMS } from '../components/Term';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGION_COLORS = {
  Bengaluru: '#5856D6', Mumbai: '#FF9500', Hyderabad: '#AF52DE',
  Gurgaon: '#007AFF', Chennai: '#34C759',
};

// Text uses 700 shades (WCAG AA on white); bars keep the brighter accent colours.
const fillTone = (pct) => {
  if (pct > 100) return { text: 'text-red-700', bar: 'bg-[#FF3B30]', hex: '#FF3B30' };
  if (pct >= 70) return { text: 'text-green-700', bar: 'bg-[#34C759]', hex: '#34C759' };
  return { text: 'text-amber-700', bar: 'bg-[#FF9500]', hex: '#FF9500' };
};

const KPICard = ({ icon: Icon, label, value, sub, accent = '#111111', testId, delay = 0 }) => (
  <div className="sp-card sp-card-lift sp-rise rounded-xl p-4" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">{label}</span>
      <Icon className="h-3.5 w-3.5 text-gray-400" strokeWidth={2} aria-hidden="true" />
    </div>
    <div className="sp-num text-[26px] font-bold leading-none" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-1.5">{sub}</div>}
  </div>
);

const approveLabel = (approving, plan) => {
  if (approving) return 'Approving…';
  return plan.planning_status === 'APPROVING' ? 'Resume approval' : `Approve · ${plan.container_count} Shipment(s)`;
};
const fillTextTone = (fill) => {
  if (fill > 100) return 'text-red-400';
  return fill >= 70 ? 'text-green-400' : 'text-amber-400';
};

export default function ConsolidationPlannerPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = canEditModule(user, 'shipment_planning');
  const [plan, setPlan] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [snapshotReady, setSnapshotReady] = useState(false);
  const [editQty, setEditQty] = useState({});
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);

  const fetchPlan = useCallback(async () => {
    setSnapshotReady(false);
    try {
      const res = await axios.get(`${API}/shipment-planning/allocation-plans/${planId}`, { headers: getAuthHeaders() });
      setPlan(res.data.plan);
      setLines(res.data.lines);
      setSnapshotReady(true);
    } catch {
      setNotice({ error: true, text: 'Could not refresh this plan. Reload it before approving.' });
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const saveOverride = async (line, key) => {
    const val = Number(editQty[key]);
    if (!Number.isInteger(val) || val < 0) return;
    setSaving(true);
    setNotice(null);
    try {
      const res = await axios.put(
        `${API}/shipment-planning/allocation-lines/${line.id}/override`,
        { allocation_line_id: line.id, manual_override_qty: val },
        { headers: getAuthHeaders() }
      );
      await fetchPlan();
      setEditQty({});
      setNotice({ error: false, text: `Quantity saved: ${val} units · ${res.data.container_count} container(s).` });
    } catch (e) {
      if (e.response?.status === 409 || e.response?.status === 404) {
        await fetchPlan();
        setEditQty({});
      }
      setNotice({ error: true, text: e.response?.data?.detail || 'Failed to save override' });
    } finally {
      setSaving(false);
    }
  };

  const approvePlan = async () => {
    setApproving(true);
    setNotice(null);
    try {
      const res = await axios.post(`${API}/shipment-planning/allocation-plans/${planId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success(`Approved! ${res.data.shipment_ids.length} container shipment(s) created`);
      navigate('/shipment-planning?tab=shipments');
    } catch (e) {
      // Dialog closes (no rethrow) so the inline notice below the header is visible.
      const msg = errorMessage(e, 'Failed to approve');
      await fetchPlan();
      setNotice({ error: true, text: msg });
    } finally {
      setApproving(false);
    }
  };

  // Group: container -> PO -> lines
  const containerGroups = useMemo(() => {
    const byContainer = {};
    lines.forEach(l => {
      const cn = l.container_number || 1;
      const c = byContainer[cn] = byContainer[cn] || { pos: {}, order: [] };
      const po = l.po_number || '—';
      if (!c.pos[po]) { c.pos[po] = []; c.order.push(po); }
      c.pos[po].push(l);
    });
    return Object.keys(byContainer).map(Number).sort((a, b) => a - b).map(cn => ({
      number: cn,
      summary: (plan?.containers || []).find(c => c.container_number === cn),
      poGroups: byContainer[cn].order.map(po => ({
        po_number: po,
        lines: byContainer[cn].pos[po],
        units: byContainer[cn].pos[po].reduce((s, l) => s + (l.final_qty || 0), 0),
        cartons: byContainer[cn].pos[po].reduce((s, l) => s + (l.cartons || 0), 0),
        cbm: byContainer[cn].pos[po].reduce((s, l) => s + (l.cbm || 0), 0),
        value: byContainer[cn].pos[po].reduce((s, l) => s + (l.shipment_value || 0), 0),
        window: byContainer[cn].pos[po][0]?.po_window_start,
        windowEnd: byContainer[cn].pos[po][0]?.po_window_end,
      }))
    }));
  }, [lines, plan]);

  if (loading) return <div role="status" className="text-center py-16 text-gray-500 sp-body text-[13px]">Loading plan…</div>;
  if (!plan) return (
    <div className="text-center py-16" data-testid="consol-plan-not-found">
      <p className="text-gray-500 text-[14px]">Plan not found</p>
      <Button variant="outline" onClick={() => navigate('/shipment-planning')} className="mt-4 h-9 text-[13px]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
    </div>
  );

  const isDraft = plan.planning_status === 'DRAFT';
  const editableLines = isDraft && canEdit;
  const regionColor = REGION_COLORS[lines[0]?.destination_region] || '#8E8E93';
  const maxCbm = plan.max_container_cbm || 67;

  return (
    <div className="space-y-5 sp-body pb-6" data-testid="consolidation-planner-page">
      {/* Header */}
      <div className="sp-rise flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/shipment-planning?tab=demand')} className="h-9 w-9 shrink-0 rounded-lg mt-1" aria-label="Back to FC demand" data-testid="back-to-demand-btn">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">FC Consolidation</h1>
              <span className="sp-num px-2.5 py-1 rounded-lg text-[13px] font-bold text-white" style={{ background: regionColor }}>{plan.fc_code}</span>
              {plan.optimized && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-50 text-purple-800" data-testid="optimized-badge">
                  ✦ OPTIMIZED · {plan.solver_status} · {plan.solve_time_ms}ms
                </span>
              )}
              <StatusBadge status={plan.planning_status} data-testid="consol-plan-status" />
              {!canEdit && (
                <span className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-600" title={viewOnlyHint(user)} data-testid="consol-view-only">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" /> View only
                </span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-gray-500">
              {plan.fc_name} · <span className="sp-num font-semibold text-gray-700">{(plan.po_numbers || []).length}</span> PO(s) · <span className="sp-num font-semibold text-gray-700">{plan.container_count}</span> × {plan.container_type} container(s)
              {plan.optimized && plan.max_containers && <> · limit {plan.max_containers} container(s)</>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {canEdit && isDraft && <CancelDraftButton planId={planId} onCancelled={fetchPlan} disabled={approving || saving} />}
        {canEdit && (isDraft || plan.planning_status === 'APPROVING') && lines.length > 0 && (
          <Button
            onClick={() => setConfirmApproveOpen(true)}
            disabled={approving || saving || !snapshotReady || !plan.total_suggested_units || Object.keys(editQty).length > 0}
            className="h-10 px-6 bg-[#34C759] hover:bg-[#2DA84E] active:scale-95 text-white text-[13px] font-bold rounded-lg shadow-sm transition-transform"
            data-testid="approve-consolidation-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {approveLabel(approving, plan)}
          </Button>
        )}
        </div>
      </div>

      {notice && <div role={notice.error ? 'alert' : 'status'} data-testid="planner-feedback" className={`flex items-start justify-between gap-3 rounded-lg border p-3 text-sm ${notice.error ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`}>
        <span data-testid="planner-feedback-message">{notice.text}</span>
        <div className="flex shrink-0 gap-1">
          {!snapshotReady && <Button size="sm" variant="outline" onClick={fetchPlan} data-testid="reload-planner-snapshot">Reload plan</Button>}
          <Button size="icon" variant="ghost" className="h-6 w-6" aria-label="Dismiss plan message" onClick={() => setNotice(null)} data-testid="dismiss-planner-feedback"><X className="h-4 w-4" /></Button>
        </div>
      </div>}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="consol-kpi-cards">
        <KPICard icon={Package} label="Unplanned at creation" value={plan.total_po_units?.toLocaleString('en-IN')} sub={<Term hint={PLANNER_TERMS.unplannedAtCreation}>Matched PO units</Term>} testId="kpi-outstanding" delay={0} />
        <KPICard icon={Boxes} label="Planned" value={plan.total_suggested_units?.toLocaleString('en-IN')} sub={`${((plan.total_suggested_units / (plan.total_po_units || 1)) * 100).toFixed(0)}% of available demand`} accent="#007AFF" testId="kpi-planned" delay={40} />
        <KPICard icon={Truck} label="Containers" value={plan.container_count} sub={`${plan.container_type} · ${maxCbm} CBM each`} accent="#AF52DE" testId="kpi-containers" delay={80} />
        <KPICard icon={Layers} label="Cartons" value={plan.total_cartons?.toLocaleString('en-IN')} testId="kpi-consol-cartons" delay={120} />
        <KPICard icon={Container} label="Total volume" value={plan.total_cbm != null ? `${plan.total_cbm.toFixed(2)} m³` : '—'} sub="CBM" testId="kpi-consol-cbm" delay={160} />
        <KPICard icon={IndianRupee} label="Ship Value" value={`₹${((plan.total_suggested_value || 0) / 100000).toFixed(1)}L`} sub={`₹${(plan.total_suggested_value || 0).toLocaleString('en-IN')}`} testId="kpi-consol-value" delay={200} />
      </div>

      {/* Container sections */}
      {containerGroups.map((cg, ci) => {
        const fill = cg.summary?.fill_percentage ?? 0;
        const tone = fillTone(fill);
        return (
          <div key={cg.number} className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm" style={{ animationDelay: `${140 + ci * 60}ms` }} data-testid={`container-section-${cg.number}`}>
            {/* Container header */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 bg-[#111] text-white">
              <div className="flex items-center gap-3">
                <Truck className="h-4 w-4 text-white/70" aria-hidden="true" />
                <span className="sp-heading text-[14px] font-black tracking-wide">CONTAINER {cg.number}</span>
                <span className="text-[11px] text-white/70">{plan.container_type}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px]">
                <span><span className="sp-num font-bold">{cg.summary?.units ?? 0}</span> <span className="text-white/70">units</span></span>
                <span><span className="sp-num font-bold">{cg.summary?.cartons ?? 0}</span> <span className="text-white/70">cartons</span></span>
                <span><span className="sp-num font-bold">{(cg.summary?.cbm ?? 0).toFixed(2)}</span> <span className="text-white/70">CBM</span></span>
                <span><span className="sp-num font-bold">₹{((cg.summary?.value ?? 0) / 100000).toFixed(1)}L</span></span>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-24 rounded-full bg-white/20 overflow-hidden">
                    <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${Math.min(fill, 100)}%` }} />
                  </div>
                  <span className={`sp-num text-[12px] font-bold ${fillTextTone(fill)}`} data-testid={`container-fill-${cg.number}`}>{fill.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            {/* PO groups */}
            {cg.poGroups.map(pg => (
              <div key={`${cg.number}-${pg.po_number}`} data-testid={`container-${cg.number}-po-${pg.po_number}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-2 bg-gray-50/80 border-y border-black/5">
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => navigate(`/retail-pos/${pg.po_number}`)} aria-label={`Open PO ${pg.po_number}`} className="sp-num px-2 py-0.5 rounded-md text-[11px] font-bold text-white hover:opacity-80 transition-opacity" style={{ background: regionColor }}>
                      {pg.po_number}
                    </button>
                    <span className="text-[11px] text-gray-600">window {pg.window || '—'} → {pg.windowEnd || '—'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
                    <span><span className="sp-num font-semibold text-gray-700">{pg.units}</span> units</span>
                    <span><span className="sp-num font-semibold text-gray-700">{pg.cartons}</span> ctns</span>
                    <span><span className="sp-num font-semibold text-gray-700">{pg.cbm.toFixed(2)}</span> CBM</span>
                    <span className="sp-num font-semibold text-gray-700">₹{pg.value.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-[12px] sp-grid-table" aria-label={`Lines for PO ${pg.po_number} in container ${cg.number}`}>
                  <tbody>
                    {pg.lines.map((line) => {
                      const key = `${line.id}`;
                      const lineTotal = lines.filter(item => item.po_number === line.po_number &&
                        (item.po_line_key || item.sku_code) === (line.po_line_key || line.sku_code))
                        .reduce((total, item) => total + (item.final_qty || 0), 0);
                      return (
                        <tr key={key} data-testid={`consol-line-${line.id}`}>
                          <td className="px-5 py-2 w-[26%]">
                            <div className="sp-num font-semibold text-gray-800">
                              {line.sku_code}
                              {line.is_split_part && <span className="ml-1.5 text-[10px] font-bold text-purple-700 uppercase">split</span>}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate max-w-[240px]">{line.product_name}</div>
                          </td>
                          <td className="px-3 py-2 text-right w-[9%]">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">Available demand</div>
                            <span className="sp-num font-semibold text-amber-700">{line.po_qty || '—'}</span>
                          </td>
                          <td className="px-3 py-2 text-right w-[9%]">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500"><Term hint={PLANNER_TERMS.freeStock}>Free Stock</Term></div>
                            <span className="sp-num text-gray-600">{line.free_to_ship_qty}</span>
                          </td>
                          <td className="px-3 py-2 text-center w-[13%] bg-blue-50/30">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">{editableLines ? 'PO line total' : 'Ship Qty'}</div>
                            {editableLines ? (
                              <div className="flex items-center justify-center gap-1">
                                <Input
                                  type="number"
                                  min={0}
                                  step={1}
                                  disabled={approving || saving}
                                  aria-label={`Total shipment quantity for ${line.sku_code} on ${line.po_number}`}
                                  value={editQty[key] !== undefined ? editQty[key] : lineTotal}
                                  onChange={(e) => setEditQty(e.target.value !== '' && Number(e.target.value) === lineTotal ? {} : { [key]: e.target.value })}
                                  className={`h-7 w-20 text-center text-[12px] sp-num font-bold bg-white ${line.manual_override_qty != null ? 'border-[#AF52DE] bg-purple-50 text-purple-800' : ''}`}
                                  data-testid={`consol-qty-input-${line.id}`}
                                />
                                {editQty[key] !== undefined && parseInt(editQty[key]) !== lineTotal && (
                                  <Button size="sm" disabled={saving || approving || !/^\d+$/.test(String(editQty[key]).trim())} title={/^\d+$/.test(String(editQty[key]).trim()) ? undefined : 'Enter a whole number of 0 or more'} onClick={() => saveOverride(line, key)} className="h-7 px-2 text-[10px] font-bold bg-[#AF52DE] hover:bg-[#9542BD] active:scale-95" data-testid={`consol-save-${line.id}`}>
                                    Save
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="sp-num font-bold text-gray-900">{line.final_qty}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right w-[8%]">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">Cartons</div>
                            <span className="sp-num text-gray-600">{line.cartons}</span>
                          </td>
                          <td className="px-3 py-2 text-right w-[8%]">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">CBM</div>
                            <span className="sp-num text-gray-600">{line.cbm?.toFixed(2)}</span>
                          </td>
                          <td className="px-3 py-2 text-right w-[10%]">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500">Value ₹</div>
                            <span className="sp-num text-gray-600">{(line.shipment_value || 0).toLocaleString('en-IN')}</span>
                          </td>
                          <td className="px-5 py-2 text-gray-600 text-[11px] leading-snug">
                            {(line.po_qty > 0 && line.final_qty < line.po_qty && !line.is_split_part) && (
                              <AlertTriangle className="h-3 w-3 text-amber-600 inline mr-1" aria-label="Ships less than the PO quantity" />
                            )}
                            {line.reason_for_suggestion}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* Footer summary */}
      {editableLines && (
        <div className="sp-glass sticky bottom-4 z-30 rounded-xl border border-black/10 shadow-lg px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-gray-600">
            <span className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-[#007AFF]" /><span className="sp-num font-bold">{plan.total_suggested_units}</span> units</span>
            <span><span className="sp-num font-bold">{plan.container_count}</span> containers</span>
            <span><span className="sp-num font-bold">{plan.total_cartons}</span> cartons</span>
            <span><span className="sp-num font-bold">{plan.total_cbm?.toFixed(1)}</span> CBM</span>
          </div>
          <Button
            onClick={() => setConfirmApproveOpen(true)}
            disabled={approving || saving || !snapshotReady || !plan.total_suggested_units || Object.keys(editQty).length > 0}
            className="h-10 px-6 bg-[#34C759] hover:bg-[#2DA84E] active:scale-95 text-white text-[13px] font-bold rounded-lg shadow-sm transition-transform"
            data-testid="approve-consolidation-footer-btn"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {approving ? 'Approving…' : `Approve · ${plan.container_count} Shipment(s)`}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmApproveOpen}
        onOpenChange={setConfirmApproveOpen}
        title={`Approve consolidation for ${plan.fc_code}?`}
        description={`Approval reserves warehouse stock and creates ${plan.container_count} container shipment${plan.container_count === 1 ? '' : 's'} to ${plan.fc_code}. Check the summary below before continuing.`}
        confirmLabel={plan.planning_status === 'APPROVING' ? 'Resume approval' : `Approve & create ${plan.container_count} shipment${plan.container_count === 1 ? '' : 's'}`}
        busyLabel="Approving…"
        onConfirm={approvePlan}
        testId="approve-consolidation-dialog"
      >
        <div className="space-y-3 text-[13px]" data-testid="approve-consolidation-summary">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-black/5 bg-gray-50 p-3">
            <dt className="text-gray-500">Total units</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">{(plan.total_suggested_units || 0).toLocaleString('en-IN')}</dd>
            <dt className="text-gray-500">Container shipments</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">{plan.container_count} × {plan.container_type}</dd>
            <dt className="text-gray-500">FC</dt>
            <dd className="text-right font-semibold text-gray-900">{plan.fc_code}{plan.fc_name ? ` · ${plan.fc_name}` : ''}</dd>
            <dt className="text-gray-500">POs</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">{(plan.po_numbers || []).length}</dd>
            <dt className="text-gray-500">Cartons / CBM</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">{(plan.total_cartons || 0).toLocaleString('en-IN')} / {(plan.total_cbm || 0).toFixed(2)}</dd>
            <dt className="text-gray-500">Value</dt>
            <dd className="sp-num text-right font-semibold text-gray-900">₹{Math.round(plan.total_suggested_value || 0).toLocaleString('en-IN')}</dd>
          </dl>
          {containerGroups.some(cg => cg.summary) && (
            <ul className="space-y-1 text-[12px] text-gray-600">
              {containerGroups.filter(cg => cg.summary).map(cg => (
                <li key={cg.number} className="flex justify-between">
                  <span>Container {cg.number}</span>
                  <span className={`sp-num font-semibold ${fillTone(cg.summary.fill_percentage ?? 0).text}`}>{(cg.summary.fill_percentage ?? 0).toFixed(1)}% fill</span>
                </li>
              ))}
            </ul>
          )}
          {(() => {
            const offFill = containerGroups.filter(cg => cg.summary?.fill_percentage != null
              && (cg.summary.fill_percentage < 70 || cg.summary.fill_percentage > 100));
            if (!offFill.length) return null;
            return (
              <p className="flex items-start gap-2 rounded-lg border border-[#FFCC00]/40 bg-[#FFFBEB] p-2.5 text-[12px] text-[#92400E]" data-testid="approve-consolidation-fill-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#B45309]" />
                <span>
                  {offFill.map(cg => `Container ${cg.number} is ${cg.summary.fill_percentage.toFixed(1)}% full${cg.summary.fill_percentage > 100 ? ' (over capacity)' : ' (under 70%)'}`).join('; ')}.
                </span>
              </p>
            );
          })()}
        </div>
      </ConfirmDialog>
    </div>
  );
}
